import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  GripVertical,
  Plus,
  Trash2,
  X,
} from 'lucide-react';

// ==================== Types ====================

interface WorkflowStep {
  step_id: string;
  type: string;
  steps?: WorkflowStep[];
  step?: WorkflowStep;
  branches?: Record<string, WorkflowStep>;
  default?: WorkflowStep;
  // GROUP
  execution_mode?: string;
  success_mode?: string;
  threshold?: number;
  wait_for_all_steps?: boolean;
  fallback_action?: string;
  short_circuit?: boolean;
  absorb_cancellation?: boolean;
  auto_fallback?: boolean;
  restart_on_go_back?: boolean;
  // POLL
  max_attempts?: number;
  interval_millis?: number;
  backoff_coefficient?: number;
  max_interval_millis?: number;
  success_condition?: string;
  terminal_condition?: string;
  // LOOP
  loop_variable_name?: string;
  data_source_expression?: string;
  // HTTP
  method?: string;
  url?: string;
  headers?: Record<string, string>;
  dynamic_headers_expression?: string;
  body_expression?: string;
  // NOVA
  journey_id?: string;
  // TRANSFORM
  expression?: string;
  status_expression?: string;
  // CONDITIONAL
  propagate_go_back_to_parent?: boolean;
  // INPUT_COLLECTOR
  required_input_keys?: string[];
  allowed_input_keys?: string[];
  auto_continue_on_receiving_all_required_inputs?: boolean;
  skippable?: boolean;
  propagate_skip_to_parent?: boolean;
  ui_mapping?: Record<string, any>;
  field_validations?: Record<string, any>;
  history_check_poll_config?: Record<string, any>;
  // Common base fields
  input_mapping?: Record<string, any>;
  summary_mapping?: string;
  go_backable?: boolean;
  continuable?: boolean;
  restartable?: boolean;
  required?: boolean;
  step_data_storage_type?: string;
  [key: string]: any;
}

export interface WorkflowEditorProps {
  workflow: any;
  onChange: (workflow: any) => void;
  readOnly?: boolean;
}

// ==================== Constants ====================

const STEP_TYPES = [
  'GROUP',
  'POLL',
  'LOOP',
  'HTTP',
  'NOVA',
  'TRANSFORM',
  'CONDITIONAL',
  'INPUT_COLLECTOR',
] as const;

const BADGE_INLINE_STYLES: Record<string, { bg: string; color: string }> = {
  GROUP: { bg: '#dbeafe', color: '#1e40af' },
  POLL: { bg: '#ede9fe', color: '#5b21b6' },
  HTTP: { bg: '#d1fae5', color: '#065f46' },
  NOVA: { bg: '#fef3c7', color: '#92400e' },
  TRANSFORM: { bg: '#fce7f3', color: '#9d174d' },
  CONDITIONAL: { bg: '#e0e7ff', color: '#3730a3' },
  LOOP: { bg: '#ccfbf1', color: '#0f766e' },
  INPUT_COLLECTOR: { bg: '#ffedd5', color: '#9a3412' },
};

const FRIENDLY_TYPE_NAMES: Record<string, string> = {
  GROUP: 'Group',
  POLL: 'Retry/Poll',
  LOOP: 'Loop',
  HTTP: 'API Call',
  NOVA: 'Sub-Journey',
  TRANSFORM: 'Transform',
  CONDITIONAL: 'Decision',
  INPUT_COLLECTOR: 'User Input',
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  GROUP: 'Contains multiple steps that run together',
  POLL: 'Retries a step until it succeeds',
  LOOP: 'Repeats a step for each item in a list',
  HTTP: 'Makes an API call to an external service',
  NOVA: 'Runs another journey as a sub-process',
  TRANSFORM: 'Processes and transforms data',
  CONDITIONAL: 'Chooses a path based on a condition',
  INPUT_COLLECTOR: 'Waits for user to provide information',
};

// ==================== Helpers ====================

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function findStepById(step: WorkflowStep, id: string): WorkflowStep | null {
  if (step.step_id === id) return step;
  if (step.steps) {
    for (const child of step.steps) {
      const found = findStepById(child, id);
      if (found) return found;
    }
  }
  if (step.step) {
    const found = findStepById(step.step, id);
    if (found) return found;
  }
  if (step.branches) {
    for (const branch of Object.values(step.branches)) {
      const found = findStepById(branch, id);
      if (found) return found;
    }
  }
  if (step.default) {
    const found = findStepById(step.default, id);
    if (found) return found;
  }
  return null;
}

/** Find the parent context for a step by ID. Returns info about where the step lives. */
function findStepParentContext(
  root: WorkflowStep,
  targetId: string
): {
  parentStep: WorkflowStep;
  container: 'steps' | 'step' | 'branches' | 'default';
  index: number;
  branchKey?: string;
} | null {
  if (root.steps) {
    for (let i = 0; i < root.steps.length; i++) {
      if (root.steps[i].step_id === targetId) {
        return { parentStep: root, container: 'steps', index: i };
      }
      const found = findStepParentContext(root.steps[i], targetId);
      if (found) return found;
    }
  }
  if (root.step) {
    if (root.step.step_id === targetId) {
      return { parentStep: root, container: 'step', index: 0 };
    }
    const found = findStepParentContext(root.step, targetId);
    if (found) return found;
  }
  if (root.branches) {
    for (const [key, branch] of Object.entries(root.branches)) {
      if (branch.step_id === targetId) {
        return { parentStep: root, container: 'branches', index: 0, branchKey: key };
      }
      const found = findStepParentContext(branch, targetId);
      if (found) return found;
    }
  }
  if (root.default) {
    if (root.default.step_id === targetId) {
      return { parentStep: root, container: 'default', index: 0 };
    }
    const found = findStepParentContext(root.default, targetId);
    if (found) return found;
  }
  return null;
}

function hasChildren(step: WorkflowStep): boolean {
  return (
    (!!step.steps && step.steps.length > 0) ||
    !!step.step ||
    (!!step.branches && Object.keys(step.branches).length > 0) ||
    !!step.default
  );
}

function canAddChild(type: string): boolean {
  return ['GROUP', 'LOOP', 'POLL', 'CONDITIONAL'].includes(type);
}

function safeJsonStringify(value: any, fallback = '{}'): string {
  try {
    return value ? JSON.stringify(value, null, 2) : fallback;
  } catch {
    return fallback;
  }
}

function safeJsonParse(text: string): any | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ==================== Drag-and-Drop Context ====================

interface DragState {
  stepId: string;
  parentStepId: string;
  index: number;
}

// ==================== StepTreeItem Component ====================

interface StepTreeItemProps {
  step: WorkflowStep;
  depth: number;
  parentType: string | null;
  parentStepId: string | null;
  index: number;
  selectedId: string | null;
  collapsedIds: Set<string>;
  readOnly: boolean;
  onSelect: (step: WorkflowStep) => void;
  onToggleCollapse: (stepId: string) => void;
  onDeleteStep: (stepId: string) => void;
  onAddChild: (parentStepId: string) => void;
  onAddBranch: (parentStepId: string) => void;
  onAddSibling: (parentStepId: string, index: number, position: 'before' | 'after') => void;
  onDuplicateStep: (stepId: string) => void;
  onDeleteBranch: (parentStepId: string, branchKey: string) => void;
  dragState: DragState | null;
  onDragStart: (state: DragState) => void;
  onDragOver: (e: React.DragEvent, targetParentStepId: string, targetIndex: number) => void;
  onDrop: (e: React.DragEvent, targetParentStepId: string, targetIndex: number) => void;
  onDragEnd: () => void;
}

const StepTreeItem: React.FC<StepTreeItemProps> = ({
  step,
  depth,
  parentType,
  parentStepId,
  index,
  selectedId,
  collapsedIds,
  readOnly,
  onSelect,
  onToggleCollapse,
  onDeleteStep,
  onAddChild,
  onAddBranch,
  onAddSibling,
  onDuplicateStep,
  onDeleteBranch,
  dragState,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const isCollapsed = collapsedIds.has(step.step_id);
  const showChildren = hasChildren(step) && !isCollapsed;
  const isSelected = selectedId === step.step_id;
  const badge = BADGE_INLINE_STYLES[step.type] || { bg: '#f3f4f6', color: '#1f2937' };
  const isRoot = depth === 0;
  const isDraggable = !readOnly && !isRoot && parentType === 'GROUP';
  const isGroupChild = parentType === 'GROUP';

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
          borderRadius: 6, cursor: 'pointer', marginBottom: 3,
          backgroundColor: isSelected ? '#dbeafe' : '#fff',
          border: isSelected ? '1px solid #93c5fd' : '1px solid #e5e7eb',
          boxShadow: isSelected ? '0 1px 3px rgba(59,130,246,0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onClick={() => onSelect(step)}
        draggable={isDraggable}
        onDragStart={(e) => {
          if (!isDraggable || !parentStepId) return;
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          onDragStart({ stepId: step.step_id, parentStepId, index });
        }}
        onDragOver={(e) => {
          if (!isGroupChild || !parentStepId) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
          onDragOver(e, parentStepId, index);
        }}
        onDrop={(e) => {
          if (!isGroupChild || !parentStepId) return;
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, parentStepId, index);
        }}
        onDragEnd={onDragEnd}
      >
        {/* Drag handle for GROUP children */}
        {isDraggable ? (
          <span style={{ cursor: 'grab', color: '#9ca3af', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <GripVertical size={12} />
          </span>
        ) : null}

        {hasChildren(step) ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(step.step_id); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#374151', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        ) : (
          <span style={{ width: 16, display: 'inline-block', textAlign: 'center', color: '#9ca3af', flexShrink: 0 }}>&#8226;</span>
        )}

        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap', backgroundColor: badge.bg, color: badge.color, flexShrink: 0 }}>
          {step.type}
        </span>

        <span style={{ fontSize: 13, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
          {step.step_id}
        </span>

      </div>

      {/* Action buttons on a separate row when selected */}
      {!readOnly && isSelected && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px', marginBottom: 4, background: '#f8fafc', borderRadius: '0 0 6px 6px', border: '1px solid #93c5fd', borderTop: 'none' }}>
          {step.type === 'CONDITIONAL' && (
            <button onClick={(e) => { e.stopPropagation(); onAddBranch(step.step_id); }} style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 4, cursor: 'pointer', color: '#4338ca', padding: '4px 10px', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Add a new branch to this conditional">
              <Plus size={10} />Branch
            </button>
          )}
          {canAddChild(step.type) && step.type !== 'CONDITIONAL' && (
            <button onClick={(e) => { e.stopPropagation(); onAddChild(step.step_id); }} style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 4, cursor: 'pointer', color: '#15803d', padding: '4px 10px', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Add child step">
              <Plus size={10} />Add Child
            </button>
          )}
          {isGroupChild && parentStepId && (
            <button onClick={(e) => { e.stopPropagation(); onAddSibling(parentStepId, index, 'after'); }} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', color: '#374151', padding: '4px 10px', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Add step after this one">
              <Plus size={10} />Add After
            </button>
          )}
          {!isRoot && (
            <button onClick={(e) => { e.stopPropagation(); onDuplicateStep(step.step_id); }} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', color: '#374151', padding: '4px 10px', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Duplicate this step">
              <Copy size={10} />Duplicate
            </button>
          )}
          {!isRoot && (
            <button onClick={(e) => { e.stopPropagation(); onDeleteStep(step.step_id); }} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 4, cursor: 'pointer', color: '#dc2626', padding: '4px 10px', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }} title="Delete this step">
              <Trash2 size={10} />Delete
            </button>
          )}
        </div>
      )}

      {showChildren && (
        <div style={{ borderLeft: '1px dashed #9ca3af', marginLeft: 12, paddingLeft: 12 }}>
          {/* GROUP steps[] children */}
          {step.steps && step.steps.map((child, idx) => (
            <StepTreeItem
              key={child.step_id + '-' + idx}
              step={child}
              depth={depth + 1}
              parentType={step.type}
              parentStepId={step.step_id}
              index={idx}
              selectedId={selectedId}
              collapsedIds={collapsedIds}
              readOnly={readOnly}
              onSelect={onSelect}
              onToggleCollapse={onToggleCollapse}
              onDeleteStep={onDeleteStep}
              onAddChild={onAddChild}
              onAddBranch={onAddBranch}
              onAddSibling={onAddSibling}
              onDuplicateStep={onDuplicateStep}
              onDeleteBranch={onDeleteBranch}
              dragState={dragState}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          ))}

          {/* LOOP/POLL single child */}
          {step.step && !step.steps && (
            <StepTreeItem
              step={step.step}
              depth={depth + 1}
              parentType={step.type}
              parentStepId={step.step_id}
              index={0}
              selectedId={selectedId}
              collapsedIds={collapsedIds}
              readOnly={readOnly}
              onSelect={onSelect}
              onToggleCollapse={onToggleCollapse}
              onDeleteStep={onDeleteStep}
              onAddChild={onAddChild}
              onAddBranch={onAddBranch}
              onAddSibling={onAddSibling}
              onDuplicateStep={onDuplicateStep}
              onDeleteBranch={onDeleteBranch}
              dragState={dragState}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          )}

          {/* CONDITIONAL branches */}
          {step.branches && Object.entries(step.branches).map(([key, branch]) => (
            <div key={key} style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 2, paddingLeft: 8 }}>
                <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>
                  Branch: {key}
                </span>
                {!readOnly && (
                  <button
                    onClick={() => onDeleteBranch(step.step_id, key)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', padding: 2, borderRadius: 4, display: 'flex', alignItems: 'center' }}
                    title={`Delete branch "${key}"`}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <StepTreeItem
                step={branch}
                depth={depth + 1}
                parentType={step.type}
                parentStepId={step.step_id}
                index={0}
                selectedId={selectedId}
                collapsedIds={collapsedIds}
                readOnly={readOnly}
                onSelect={onSelect}
                onToggleCollapse={onToggleCollapse}
                onDeleteStep={onDeleteStep}
                onAddChild={onAddChild}
                onAddBranch={onAddBranch}
                onAddSibling={onAddSibling}
                onDuplicateStep={onDuplicateStep}
                onDeleteBranch={onDeleteBranch}
                dragState={dragState}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
              />
            </div>
          ))}

          {/* CONDITIONAL default branch */}
          {step.type === 'CONDITIONAL' && step.default && (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 2, paddingLeft: 8 }}>
                <span style={{ background: '#f3f4f6', color: '#374151', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>
                  Default
                </span>
              </div>
              <StepTreeItem
                step={step.default}
                depth={depth + 1}
                parentType={step.type}
                parentStepId={step.step_id}
                index={0}
                selectedId={selectedId}
                collapsedIds={collapsedIds}
                readOnly={readOnly}
                onSelect={onSelect}
                onToggleCollapse={onToggleCollapse}
                onDeleteStep={onDeleteStep}
                onAddChild={onAddChild}
                onAddBranch={onAddBranch}
                onAddSibling={onAddSibling}
                onDuplicateStep={onDuplicateStep}
                onDeleteBranch={onDeleteBranch}
                dragState={dragState}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== JsonTextField Component ====================

/** A textarea that displays a JSON object as formatted text. On blur it parses and writes back. */
const JsonTextField: React.FC<{
  value: any;
  onChange: (parsed: any) => void;
  rows?: number;
  placeholder?: string;
  readOnly?: boolean;
}> = ({ value, onChange, rows = 4, placeholder, readOnly }) => {
  const [text, setText] = useState(() => safeJsonStringify(value));

  // Re-sync when external value changes (e.g. selecting a different step)
  const serialized = safeJsonStringify(value);
  const prevSerialized = useRef(serialized);
  useEffect(() => {
    if (serialized !== prevSerialized.current) {
      setText(serialized);
      prevSerialized.current = serialized;
    }
  }, [serialized]);

  const handleBlur = useCallback(() => {
    const parsed = safeJsonParse(text);
    if (parsed !== null) {
      onChange(parsed);
    }
    // If parse fails, leave the text as-is so the user can fix it
  }, [text, onChange]);

  return (
    <textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      rows={rows}
      className="w-full border rounded px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      placeholder={placeholder}
      readOnly={readOnly}
      spellCheck={false}
    />
  );
};

// ==================== Step Editor Sections ====================

interface StepEditorProps {
  step: WorkflowStep;
  /** Immutable updater: receives a callback that mutates a draft step in the cloned tree */
  onFieldChange: (stepId: string, updater: (draft: WorkflowStep) => void) => void;
  readOnly: boolean;
}

// Basic Info section
const BasicInfoSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Basic Info</h3>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Step Name
        </label>
        <input
          value={step.step_id || ''}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.step_id = val; });
          }}
          type="text"
          className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., verify_user_details"
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Unique identifier for this step
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Step Type
        </label>
        <select
          value={step.type || 'TRANSFORM'}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => {
              d.type = val;
              if (val === 'GROUP' && !d.steps) {
                d.steps = [];
                d.success_mode = 'AND';
              }
              if (val === 'POLL' && !d.max_attempts) {
                d.max_attempts = 10;
                d.interval_millis = 2000;
              }
              if (val === 'HTTP' && !d.method) {
                d.method = 'POST';
              }
              if (val === 'CONDITIONAL' && !d.branches) {
                d.branches = {};
              }
            });
          }}
          className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={readOnly}
        >
          {STEP_TYPES.map((type) => (
            <option key={type} value={type}>
              {FRIENDLY_TYPE_NAMES[type]}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {TYPE_DESCRIPTIONS[step.type] || ''}
        </p>
      </div>
    </div>
  </div>
);

// Base fields section (go_backable, continuable, restartable, required, step_data_storage_type)
const BaseFieldsSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Advanced Flags</h3>
    <div className="grid grid-cols-2 gap-3">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!step.go_backable}
          onChange={(e) => {
            const val = e.target.checked;
            onFieldChange(step.step_id, (d) => { d.go_backable = val; });
          }}
          className="rounded"
          disabled={readOnly}
        />
        Go-backable
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!step.continuable}
          onChange={(e) => {
            const val = e.target.checked;
            onFieldChange(step.step_id, (d) => { d.continuable = val; });
          }}
          className="rounded"
          disabled={readOnly}
        />
        Continuable
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!step.restartable}
          onChange={(e) => {
            const val = e.target.checked;
            onFieldChange(step.step_id, (d) => { d.restartable = val; });
          }}
          className="rounded"
          disabled={readOnly}
        />
        Restartable
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!step.required}
          onChange={(e) => {
            const val = e.target.checked;
            onFieldChange(step.step_id, (d) => { d.required = val; });
          }}
          className="rounded"
          disabled={readOnly}
        />
        Required
      </label>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Step Data Storage Type
      </label>
      <select
        value={step.step_data_storage_type || ''}
        onChange={(e) => {
          const val = e.target.value || undefined;
          onFieldChange(step.step_id, (d) => { d.step_data_storage_type = val; });
        }}
        className="w-full border rounded px-3 py-2 text-sm"
        disabled={readOnly}
      >
        <option value="">Default</option>
        <option value="IN_MEMORY">In Memory</option>
        <option value="PERSISTENT">Persistent</option>
      </select>
    </div>
  </div>
);

// GROUP section
const GroupSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Group Settings</h3>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Execution Mode
        </label>
        <select
          value={step.execution_mode || 'SEQUENTIAL'}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.execution_mode = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="SEQUENTIAL">Sequential</option>
          <option value="PARALLEL">Parallel</option>
        </select>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Run steps one at a time or all at once
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Success Requirement
        </label>
        <select
          value={step.success_mode || 'AND'}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.success_mode = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="AND">All steps must succeed (AND)</option>
          <option value="OR">Any step succeeds (OR)</option>
          <option value="THRESHOLD">Minimum count (THRESHOLD)</option>
        </select>
        <p className="text-[11px] text-gray-500 mt-0.5">
          When is this group considered successful?
        </p>
      </div>
    </div>
    {step.success_mode === 'THRESHOLD' && (
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Threshold Count
        </label>
        <input
          value={step.threshold ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            onFieldChange(step.step_id, (d) => { d.threshold = val; });
          }}
          type="number"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="Minimum number of steps that must succeed"
          readOnly={readOnly}
        />
      </div>
    )}
    <div className="grid grid-cols-2 gap-4 mt-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          If Step Fails
        </label>
        <select
          value={step.fallback_action || ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            onFieldChange(step.step_id, (d) => { d.fallback_action = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="">Continue to next step</option>
          <option value="CONTINUE">Skip and continue</option>
          <option value="GO_BACK">Go back to previous</option>
          <option value="FAIL">Fail immediately</option>
        </select>
        <p className="text-[11px] text-gray-500 mt-0.5">
          What happens when a step fails?
        </p>
      </div>
      <div className="flex items-end pb-1">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.wait_for_all_steps}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.wait_for_all_steps = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Wait for all steps
        </label>
      </div>
    </div>
    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
      <p className="text-xs font-medium text-gray-600 mb-2">Behavior Options</p>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.short_circuit}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.short_circuit = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Stop on first failure
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.absorb_cancellation}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.absorb_cancellation = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Ignore cancellations
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.auto_fallback}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.auto_fallback = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Auto-recover on failure
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.restart_on_go_back}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.restart_on_go_back = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Restart when going back
        </label>
      </div>
    </div>
  </div>
);

// POLL section
const PollSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Retry Settings</h3>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Maximum Retries
        </label>
        <input
          value={step.max_attempts ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            onFieldChange(step.step_id, (d) => { d.max_attempts = val; });
          }}
          type="number"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="10"
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          How many times to retry before giving up
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Wait Between Retries
        </label>
        <div className="flex items-center gap-2">
          <input
            value={step.interval_millis ?? ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : undefined;
              onFieldChange(step.step_id, (d) => { d.interval_millis = val; });
            }}
            type="number"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="2000"
            readOnly={readOnly}
          />
          <span className="text-xs text-gray-500 whitespace-nowrap">ms</span>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Initial delay between attempts
        </p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4 mt-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Delay Multiplier
        </label>
        <input
          value={step.backoff_coefficient ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            onFieldChange(step.step_id, (d) => { d.backoff_coefficient = val; });
          }}
          type="number"
          step="0.1"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="1.5"
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Increase wait time by this factor each retry
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Maximum Wait Time
        </label>
        <div className="flex items-center gap-2">
          <input
            value={step.max_interval_millis ?? ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : undefined;
              onFieldChange(step.step_id, (d) => { d.max_interval_millis = val; });
            }}
            type="number"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="10000"
            readOnly={readOnly}
          />
          <span className="text-xs text-gray-500 whitespace-nowrap">ms</span>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Cap on the delay between retries
        </p>
      </div>
    </div>
    <div className="mt-4">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        When to Consider Successful
      </label>
      <textarea
        value={step.success_condition || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.success_condition = val; });
        }}
        rows={3}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="data.steps.byId[data.currentStepId]?.data?.statusCode == 200"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Expression that returns true when the step succeeds
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        When to Stop Retrying (optional)
      </label>
      <textarea
        value={step.terminal_condition || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.terminal_condition = val; });
        }}
        rows={2}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="Leave empty to retry until max attempts"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Stop retrying immediately if this is true
      </p>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          If All Retries Fail
        </label>
        <select
          value={step.fallback_action || ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            onFieldChange(step.step_id, (d) => { d.fallback_action = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="">Mark as failed</option>
          <option value="CONTINUE">Skip and continue</option>
          <option value="GO_BACK">Go back to previous</option>
          <option value="FAIL">Fail immediately</option>
        </select>
      </div>
      <div className="flex items-center pt-5">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!step.auto_fallback}
            onChange={(e) => {
              const val = e.target.checked;
              onFieldChange(step.step_id, (d) => { d.auto_fallback = val; });
            }}
            className="rounded"
            disabled={readOnly}
          />
          Auto-recover on failure
        </label>
      </div>
    </div>
  </div>
);

// LOOP section
const LoopSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Loop Settings</h3>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Item Variable Name
        </label>
        <input
          value={step.loop_variable_name || ''}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.loop_variable_name = val; });
          }}
          type="text"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="item"
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Name to access each item in the loop
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Success Requirement
        </label>
        <select
          value={step.success_mode || 'AND'}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.success_mode = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="AND">All iterations must succeed</option>
          <option value="OR">Any iteration succeeds</option>
          <option value="THRESHOLD">Minimum count (THRESHOLD)</option>
        </select>
      </div>
    </div>
    {step.success_mode === 'THRESHOLD' && (
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Threshold Count
        </label>
        <input
          value={step.threshold ?? ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            onFieldChange(step.step_id, (d) => { d.threshold = val; });
          }}
          type="number"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="Minimum number of iterations that must succeed"
          readOnly={readOnly}
        />
      </div>
    )}
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Data to Loop Over
      </label>
      <textarea
        value={step.data_source_expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.data_source_expression = val; });
        }}
        rows={4}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="data.steps.byId['previous_step']?.data?.items"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Expression that returns a list of items to iterate
      </p>
    </div>
    <div className="grid grid-cols-2 gap-4 mt-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Run Iterations
        </label>
        <select
          value={step.execution_mode || ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            onFieldChange(step.step_id, (d) => { d.execution_mode = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="">One at a time (Sequential)</option>
          <option value="PARALLEL">All at once (Parallel)</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          If Iteration Fails
        </label>
        <select
          value={step.fallback_action || ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            onFieldChange(step.step_id, (d) => { d.fallback_action = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="">Stop the loop</option>
          <option value="CONTINUE">Skip and continue</option>
          <option value="GO_BACK">Go back</option>
          <option value="FAIL">Fail immediately</option>
        </select>
      </div>
    </div>
  </div>
);

// HTTP section
const HttpSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">API Call Settings</h3>
    <div className="grid grid-cols-4 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
        <select
          value={step.method || 'GET'}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.method = val; });
          }}
          className="w-full border rounded px-3 py-2 text-sm"
          disabled={readOnly}
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>
      <div className="col-span-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          API Endpoint URL
        </label>
        <input
          value={step.url || ''}
          onChange={(e) => {
            const val = e.target.value;
            onFieldChange(step.step_id, (d) => { d.url = val; });
          }}
          type="text"
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="https://api.example.com/endpoint"
          readOnly={readOnly}
        />
      </div>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Static Headers (JSON format)
      </label>
      <JsonTextField
        value={step.headers}
        onChange={(parsed) => {
          onFieldChange(step.step_id, (d) => { d.headers = parsed; });
        }}
        rows={3}
        placeholder='{"Content-Type": "application/json"}'
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Fixed headers to include with the request
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Dynamic Headers
      </label>
      <textarea
        value={step.dynamic_headers_expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.dynamic_headers_expression = val; });
        }}
        rows={2}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="['Authorization': 'Bearer ' + data.token]"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Headers built from workflow data
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Request Body
      </label>
      <textarea
        value={step.body_expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.body_expression = val; });
        }}
        rows={4}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="['name': data.inputs.byName['name']?.value]"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Data to send in the request body
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        When to Consider Successful
      </label>
      <textarea
        value={step.success_condition || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.success_condition = val; });
        }}
        rows={2}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="data.steps.byId[data.currentStepId]?.data?.statusCode == 200"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Expression that returns true when the API call succeeds
      </p>
    </div>
  </div>
);

// NOVA section
const NovaSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Sub-Journey Settings</h3>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Journey to Run
      </label>
      <input
        value={step.journey_id || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.journey_id = val; });
        }}
        type="text"
        className="w-full border rounded px-3 py-2 text-sm"
        placeholder="e.g., aadhaar_verification_journey"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        ID of another journey to execute as part of this workflow
      </p>
    </div>
  </div>
);

// TRANSFORM section
const TransformSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Data Processing</h3>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Transform Logic
      </label>
      <textarea
        value={step.expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.expression = val; });
        }}
        rows={6}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="def result = data.steps.byId['previous']?.data;; return [processed_value: result?.value]"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Process data from previous steps and return transformed result
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Status Override (optional)
      </label>
      <textarea
        value={step.status_expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.status_expression = val; });
        }}
        rows={2}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="Leave empty for default behavior"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Custom logic to determine step success/failure status
      </p>
    </div>
  </div>
);

// CONDITIONAL section
const ConditionalSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Decision Settings</h3>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Condition Expression
      </label>
      <textarea
        value={step.expression || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.expression = val; });
        }}
        rows={4}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="data.steps.byId['verify_user']?.data?.verified == true"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        Expression that determines which branch to run
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        If Branch Fails
      </label>
      <select
        value={step.fallback_action || ''}
        onChange={(e) => {
          const val = e.target.value || undefined;
          onFieldChange(step.step_id, (d) => { d.fallback_action = val; });
        }}
        className="w-full border rounded px-3 py-2 text-sm"
        disabled={readOnly}
      >
        <option value="">Mark as failed</option>
        <option value="CONTINUE">Skip and continue</option>
        <option value="GO_BACK">Go back to previous</option>
        <option value="FAIL">Fail immediately</option>
      </select>
    </div>
    <div className="mt-3 flex items-center gap-4">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!step.propagate_go_back_to_parent}
          onChange={(e) => {
            const val = e.target.checked;
            onFieldChange(step.step_id, (d) => { d.propagate_go_back_to_parent = val; });
          }}
          className="rounded"
          disabled={readOnly}
        />
        Pass "go back" to parent workflow
      </label>
    </div>
    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
      <p className="text-xs font-medium text-gray-600 mb-2">Branches</p>
      <div className="text-xs text-gray-600">
        {step.branches && Object.keys(step.branches).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {Object.keys(step.branches).map((key) => (
              <span key={key} style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4 }}>
                {key}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">No branches defined yet. Use "+ Branch" button in the tree.</span>
        )}
        {step.default && (
          <div className="mt-2">
            <span style={{ background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 4 }}>
              default
            </span>
          </div>
        )}
        {!step.default && !readOnly && (
          <div className="mt-2">
            <button
              onClick={() => {
                onFieldChange(step.step_id, (d) => {
                  d.default = {
                    step_id: 'default_step_' + Date.now(),
                    type: 'TRANSFORM',
                    expression: 'return [:]',
                    summary_mapping: "return data.steps.byId[data.currentStepId]?.data",
                  };
                });
              }}
              style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', color: '#374151', padding: '2px 10px', fontSize: 11, fontWeight: 500 }}
            >
              <Plus size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
              Add default branch
            </button>
          </div>
        )}
      </div>
    </div>
    <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
      <strong>Tip:</strong> Branches appear as child steps in the workflow tree on the left.
      Use "+ Branch" to add named branches.
    </div>
  </div>
);

// INPUT_COLLECTOR section
const InputCollectorSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => {
  const [requiredKeysStr, setRequiredKeysStr] = useState(
    () => (step.required_input_keys || []).join(', ')
  );
  const [allowedKeysStr, setAllowedKeysStr] = useState(
    () => (step.allowed_input_keys || []).join(', ')
  );

  // Re-sync when step changes
  const stepId = step.step_id;
  useEffect(() => {
    setRequiredKeysStr((step.required_input_keys || []).join(', '));
    setAllowedKeysStr((step.allowed_input_keys || []).join(', '));
  }, [stepId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <h3 className="font-medium text-gray-700 mb-3 text-sm">User Input Settings</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Required Fields
          </label>
          <input
            value={requiredKeysStr}
            onChange={(e) => setRequiredKeysStr(e.target.value)}
            onBlur={() => {
              const keys = requiredKeysStr
                ? requiredKeysStr.split(',').map((s) => s.trim()).filter(Boolean)
                : [];
              onFieldChange(step.step_id, (d) => { d.required_input_keys = keys; });
            }}
            type="text"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="mobile_number, email"
            readOnly={readOnly}
          />
          <p className="text-[11px] text-gray-500 mt-0.5">
            Must be provided to proceed (comma separated)
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Accepted Fields
          </label>
          <input
            value={allowedKeysStr}
            onChange={(e) => setAllowedKeysStr(e.target.value)}
            onBlur={() => {
              const keys = allowedKeysStr
                ? allowedKeysStr.split(',').map((s) => s.trim()).filter(Boolean)
                : [];
              onFieldChange(step.step_id, (d) => { d.allowed_input_keys = keys; });
            }}
            type="text"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="mobile_number, email, name"
            readOnly={readOnly}
          />
          <p className="text-[11px] text-gray-500 mt-0.5">
            All fields this step will accept (comma separated)
          </p>
        </div>
      </div>
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs font-medium text-gray-600 mb-2">Behavior Options</p>
        <div className="grid grid-cols-1 gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!step.auto_continue_on_receiving_all_required_inputs}
              onChange={(e) => {
                const val = e.target.checked;
                onFieldChange(step.step_id, (d) => { d.auto_continue_on_receiving_all_required_inputs = val; });
              }}
              className="rounded"
              disabled={readOnly}
            />
            Automatically continue when all required fields are filled
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!step.skippable}
              onChange={(e) => {
                const val = e.target.checked;
                onFieldChange(step.step_id, (d) => { d.skippable = val; });
              }}
              className="rounded"
              disabled={readOnly}
            />
            Allow user to skip this step
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!step.propagate_skip_to_parent}
              onChange={(e) => {
                const val = e.target.checked;
                onFieldChange(step.step_id, (d) => { d.propagate_skip_to_parent = val; });
              }}
              className="rounded"
              disabled={readOnly}
            />
            If skipped, also skip parent workflow
          </label>
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          UI Screen Mapping
        </label>
        <JsonTextField
          value={step.ui_mapping}
          onChange={(parsed) => {
            onFieldChange(step.step_id, (d) => { d.ui_mapping = parsed; });
          }}
          rows={2}
          placeholder='{"INIT": "mobile_input_screen"}'
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Which UI screen to show for each state
        </p>
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Field Validation Rules
        </label>
        <JsonTextField
          value={step.field_validations}
          onChange={(parsed) => {
            onFieldChange(step.step_id, (d) => { d.field_validations = parsed; });
          }}
          rows={4}
          placeholder='{"mobile_number": "return [valid: data.value?.matches(\"[0-9]{10}\")]"}'
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Validation expressions for each field
        </p>
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Load from History
        </label>
        <JsonTextField
          value={step.history_check_poll_config}
          onChange={(parsed) => {
            onFieldChange(step.step_id, (d) => {
              if (parsed && Object.keys(parsed).length === 0) {
                delete d.history_check_poll_config;
              } else {
                d.history_check_poll_config = parsed;
              }
            });
          }}
          rows={4}
          placeholder='{"input_names": ["mobile_number"], "timeout_millis": 5000}'
          readOnly={readOnly}
        />
        <p className="text-[11px] text-gray-500 mt-0.5">
          Try to auto-fill fields from previous workflow data
        </p>
      </div>
    </div>
  );
};

// Common Data Mappings section
const DataMappingsSection: React.FC<StepEditorProps> = ({ step, onFieldChange, readOnly }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <h3 className="font-medium text-gray-700 mb-3 text-sm">Data Mappings</h3>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Input Data Mapping
      </label>
      <JsonTextField
        value={step.input_mapping}
        onChange={(parsed) => {
          onFieldChange(step.step_id, (d) => { d.input_mapping = parsed; });
        }}
        rows={4}
        placeholder='{"INIT": "return [field: data.inputs.byName[\"field\"]?.value]"}'
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        How to extract and format input data for this step
      </p>
    </div>
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Output Summary
      </label>
      <textarea
        value={step.summary_mapping || ''}
        onChange={(e) => {
          const val = e.target.value;
          onFieldChange(step.step_id, (d) => { d.summary_mapping = val; });
        }}
        rows={2}
        className="w-full border rounded px-3 py-2 text-sm font-mono"
        placeholder="return data.steps.byId[data.currentStepId]?.data"
        readOnly={readOnly}
      />
      <p className="text-[11px] text-gray-500 mt-0.5">
        What data to show in step summary/result
      </p>
    </div>
  </div>
);

// JSON Preview section
const JsonPreviewSection: React.FC<{ step: WorkflowStep }> = ({ step }) => {
  const jsonText = useMemo(() => JSON.stringify(step, null, 2), [step]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(jsonText).catch(() => {
      // Fallback: do nothing
    });
  }, [jsonText]);

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-700 text-sm">Raw Configuration</h3>
        <button
          onClick={handleCopy}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded flex items-center gap-1"
        >
          <Copy size={12} /> Copy
        </button>
      </div>
      <pre className="max-h-[300px] overflow-auto bg-gray-900 text-green-400 p-3 rounded text-xs">
        {jsonText}
      </pre>
    </div>
  );
};

// ==================== Main WorkflowEditor Component ====================

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  workflow: workflowProp,
  onChange,
  readOnly = false,
}) => {
  // Deep clone once on initial load and when the prop identity changes
  const [internalWorkflow, setInternalWorkflow] = useState<WorkflowStep>(() =>
    deepClone(workflowProp)
  );
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [dragState, setDragState] = useState<DragState | null>(null);

  // Inline branch creation state (replaces window.prompt)
  const [addingBranchForStepId, setAddingBranchForStepId] = useState<string | null>(null);
  const [newBranchKey, setNewBranchKey] = useState('');
  const [branchError, setBranchError] = useState<string | null>(null);

  // Re-clone when the external workflow prop changes identity (not on internal edits)
  const prevPropRef = useRef(workflowProp);
  useEffect(() => {
    if (workflowProp !== prevPropRef.current) {
      prevPropRef.current = workflowProp;
      setInternalWorkflow(deepClone(workflowProp));
      setSelectedStepId(null);
      setCollapsedIds(new Set());
    }
  }, [workflowProp]);

  /**
   * Immutable update helper. Deep-clones the workflow, finds the step by ID in the clone,
   * applies the updater mutation to the clone, sets state, and calls onChange.
   */
  const applyStepUpdate = useCallback(
    (stepId: string, updater: (draft: WorkflowStep) => void) => {
      setInternalWorkflow((prev) => {
        const cloned = deepClone(prev);
        const target = findStepById(cloned, stepId);
        if (target) {
          const oldStepId = target.step_id;
          updater(target);
          // If step_id was renamed, update selectedStepId to track the new value
          if (target.step_id !== oldStepId) {
            setSelectedStepId(target.step_id);
          }
        }
        onChange(deepClone(cloned));
        return cloned;
      });
    },
    [onChange]
  );

  /**
   * Apply a structural update to the whole workflow tree (not specific to a step ID).
   * Takes a mutator that operates on the cloned root.
   */
  const applyTreeUpdate = useCallback(
    (mutator: (draft: WorkflowStep) => void) => {
      setInternalWorkflow((prev) => {
        const cloned = deepClone(prev);
        mutator(cloned);
        onChange(deepClone(cloned));
        return cloned;
      });
    },
    [onChange]
  );

  const selectedStep = useMemo(() => {
    if (!selectedStepId) return null;
    return findStepById(internalWorkflow, selectedStepId);
  }, [internalWorkflow, selectedStepId]);

  // Tree actions
  const handleSelect = useCallback((step: WorkflowStep) => {
    setSelectedStepId(step.step_id);
  }, []);

  const handleToggleCollapse = useCallback((stepId: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }, []);

  const handleDeleteStep = useCallback(
    (stepId: string) => {
      if (!window.confirm(`Are you sure you want to delete this step?`)) return;
      applyTreeUpdate((root) => {
        const ctx = findStepParentContext(root, stepId);
        if (!ctx) return;

        if (ctx.container === 'steps' && ctx.parentStep.steps) {
          ctx.parentStep.steps.splice(ctx.index, 1);
        } else if (ctx.container === 'step') {
          delete ctx.parentStep.step;
        } else if (ctx.container === 'branches' && ctx.branchKey && ctx.parentStep.branches) {
          delete ctx.parentStep.branches[ctx.branchKey];
        } else if (ctx.container === 'default') {
          delete ctx.parentStep.default;
        }
      });
      if (selectedStepId === stepId) setSelectedStepId(null);
    },
    [applyTreeUpdate, selectedStepId]
  );

  const handleAddChild = useCallback(
    (parentStepId: string) => {
      applyTreeUpdate((root) => {
        const parent = findStepById(root, parentStepId);
        if (!parent) return;

        const newStep: WorkflowStep = {
          step_id: 'new_step_' + Date.now(),
          type: 'TRANSFORM',
          expression: 'return [:]',
          summary_mapping: "return data.steps.byId[data.currentStepId]?.data",
        };

        if (parent.type === 'GROUP') {
          if (!parent.steps) parent.steps = [];
          parent.steps.push(newStep);
        } else if (parent.type === 'POLL' || parent.type === 'LOOP') {
          if (!parent.step) {
            parent.step = newStep;
          }
          // Already has a child — do nothing
        }
      });
    },
    [applyTreeUpdate]
  );

  const handleAddBranch = useCallback(
    (parentStepId: string) => {
      setAddingBranchForStepId(parentStepId);
      setNewBranchKey('');
      setBranchError(null);
    },
    []
  );

  const confirmAddBranch = useCallback(() => {
    if (!addingBranchForStepId || !newBranchKey.trim()) return;
    const key = newBranchKey.trim();

    applyTreeUpdate((root) => {
      const parent = findStepById(root, addingBranchForStepId);
      if (!parent || parent.type !== 'CONDITIONAL') return;
      if (!parent.branches) parent.branches = {};
      if (parent.branches[key]) {
        setBranchError(`Branch "${key}" already exists.`);
        return;
      }
      parent.branches[key] = {
        step_id: 'branch_' + key + '_' + Date.now(),
        type: 'TRANSFORM',
        expression: 'return [:]',
        summary_mapping: "return data.steps.byId[data.currentStepId]?.data",
      };
      setAddingBranchForStepId(null);
      setNewBranchKey('');
      setBranchError(null);
    });
  }, [addingBranchForStepId, newBranchKey, applyTreeUpdate]);

  const cancelAddBranch = useCallback(() => {
    setAddingBranchForStepId(null);
    setNewBranchKey('');
    setBranchError(null);
  }, []);

  const handleDeleteBranch = useCallback(
    (parentStepId: string, branchKey: string) => {
      if (!window.confirm(`Delete branch "${branchKey}"?`)) return;
      applyTreeUpdate((root) => {
        const parent = findStepById(root, parentStepId);
        if (!parent || !parent.branches) return;
        delete parent.branches[branchKey];
      });
    },
    [applyTreeUpdate]
  );

  const handleDuplicateStep = useCallback(
    (stepId: string) => {
      applyTreeUpdate((root) => {
        const ctx = findStepParentContext(root, stepId);
        if (!ctx || ctx.container !== 'steps' || !ctx.parentStep.steps) return;
        const original = ctx.parentStep.steps[ctx.index];
        const clone: WorkflowStep = deepClone(original);
        clone.step_id = original.step_id + '_copy_' + Date.now();
        ctx.parentStep.steps.splice(ctx.index + 1, 0, clone);
      });
    },
    [applyTreeUpdate]
  );

  const handleAddSibling = useCallback(
    (parentStepId: string, index: number, position: 'before' | 'after') => {
      applyTreeUpdate((root) => {
        const parent = findStepById(root, parentStepId);
        if (!parent || !parent.steps) return;

        const newStep: WorkflowStep = {
          step_id: 'new_step_' + Date.now(),
          type: 'TRANSFORM',
          expression: 'return [:]',
          summary_mapping: "return data.steps.byId[data.currentStepId]?.data",
        };
        const insertIdx = position === 'before' ? index : index + 1;
        parent.steps.splice(insertIdx, 0, newStep);
      });
    },
    [applyTreeUpdate]
  );

  // Drag-and-drop handlers for reordering within GROUP steps[]
  const handleDragStart = useCallback((state: DragState) => {
    setDragState(state);
  }, []);

  const handleDragOver = useCallback((_e: React.DragEvent, _targetParentStepId: string, _targetIndex: number) => {
    // Just allow the drop — visual feedback is handled by CSS
  }, []);

  const handleDrop = useCallback(
    (_e: React.DragEvent, targetParentStepId: string, targetIndex: number) => {
      if (!dragState) return;
      // Only allow reorder within the same parent GROUP
      if (dragState.parentStepId !== targetParentStepId) {
        setDragState(null);
        return;
      }

      const fromIndex = dragState.index;
      if (fromIndex === targetIndex) {
        setDragState(null);
        return;
      }

      applyTreeUpdate((root) => {
        const parent = findStepById(root, targetParentStepId);
        if (!parent || !parent.steps) return;
        const [moved] = parent.steps.splice(fromIndex, 1);
        const adjustedTarget = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
        parent.steps.splice(adjustedTarget, 0, moved);
      });

      setDragState(null);
    },
    [dragState, applyTreeUpdate]
  );

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  return (
    <div style={{ display: 'flex', height: '100%', color: '#1f2937', fontFamily: 'Inter, system-ui, -apple-system, sans-serif', fontSize: 14 }}>
      {/* Left Panel - Tree View */}
      <div style={{ width: selectedStep ? '40%' : '100%', borderRight: selectedStep ? '1px solid #e5e7eb' : 'none', backgroundColor: '#fff', overflow: 'auto', padding: 16 }}>
        <div>
          <StepTreeItem
            step={internalWorkflow}
            depth={0}
            parentType={null}
            parentStepId={null}
            index={0}
            selectedId={selectedStepId}
            collapsedIds={collapsedIds}
            readOnly={readOnly}
            onSelect={handleSelect}
            onToggleCollapse={handleToggleCollapse}
            onDeleteStep={handleDeleteStep}
            onAddChild={handleAddChild}
            onAddBranch={handleAddBranch}
            onAddSibling={handleAddSibling}
            onDuplicateStep={handleDuplicateStep}
            onDeleteBranch={handleDeleteBranch}
            dragState={dragState}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />
        </div>

        {/* Inline branch creation form */}
        {addingBranchForStepId && (
          <div style={{ margin: '12px 0', padding: 12, border: '1px solid #c7d2fe', borderRadius: 6, background: '#eef2ff' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#4338ca', marginBottom: 8 }}>
              New Branch
            </div>
            <input
              type="text"
              value={newBranchKey}
              onChange={(e) => { setNewBranchKey(e.target.value); setBranchError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmAddBranch(); if (e.key === 'Escape') cancelAddBranch(); }}
              placeholder='e.g., "true", "approved", "verified"'
              autoFocus
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 13, marginBottom: 6 }}
            />
            {branchError && (
              <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 6 }}>{branchError}</div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={confirmAddBranch}
                disabled={!newBranchKey.trim()}
                style={{ border: '1px solid #818cf8', background: '#6366f1', borderRadius: 4, cursor: newBranchKey.trim() ? 'pointer' : 'not-allowed', color: '#fff', padding: '4px 12px', fontSize: 12, fontWeight: 500, opacity: newBranchKey.trim() ? 1 : 0.5 }}
              >
                Add
              </button>
              <button
                onClick={cancelAddBranch}
                style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', color: '#374151', padding: '4px 12px', fontSize: 12, fontWeight: 500 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Editor (only shown when step selected) */}
      {selectedStep && (
      <div style={{ width: '60%', backgroundColor: '#fafafa', overflow: 'auto', padding: 0 }}>
        {(
          <div>
            {/* Editor header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4, backgroundColor: (BADGE_INLINE_STYLES[selectedStep.type] || { bg: '#f3f4f6' }).bg, color: (BADGE_INLINE_STYLES[selectedStep.type] || { color: '#1f2937' }).color }}>
                  {FRIENDLY_TYPE_NAMES[selectedStep.type] || selectedStep.type}
                </span>
                <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 14, fontFamily: 'monospace' }}>
                  {selectedStep.step_id}
                </span>
              </div>
              <button
                onClick={() => setSelectedStepId(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 4 }}
                title="Close editor"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 16 }}>

            <div className="space-y-4">
              {/* Basic Info - always shown */}
              <BasicInfoSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />

              {/* Type-specific sections */}
              {selectedStep.type === 'GROUP' && (
                <GroupSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'POLL' && (
                <PollSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'LOOP' && (
                <LoopSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'HTTP' && (
                <HttpSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'NOVA' && (
                <NovaSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'TRANSFORM' && (
                <TransformSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'CONDITIONAL' && (
                <ConditionalSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}
              {selectedStep.type === 'INPUT_COLLECTOR' && (
                <InputCollectorSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />
              )}

              {/* Common data mappings - always shown */}
              <DataMappingsSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />

              {/* Base fields (go_backable, continuable, etc.) - always shown */}
              <BaseFieldsSection step={selectedStep} onFieldChange={applyStepUpdate} readOnly={readOnly} />

              {/* JSON Preview - always shown */}
              <JsonPreviewSection step={selectedStep} />
            </div>
          </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
